import axios, { ResponseType } from 'axios';
import moment from 'moment';
import { APIResponse, TokenData, UserLogin } from '@tempu/components/Classes'
import { BusinessUser, UserBusiness, UserBusinessAccess, Location, Posting, EditPostingInfo, EditPostingPayment, Business, EditBusinessData, CreatePostingJob, OnboardingUser, BankAccountForm, BankAccount, PaymentLog, MoneySummary, UserInfoStats, PostingUserStatus, User } from './Classes';

const ROOT_URL = process.env.REACT_APP_API_URL
const PAGE_AUTH = process.env.REACT_APP_PAGE_AUTH;

export const PAGE = process.env.REACT_APP_PAGE;
export const CDN_URL = process.env.REACT_APP_CDN_URL;
export const CDN_ROOT = process.env.REACT_APP_CDN_ROOT;
export const DEBUG = process.env.REACT_APP_DEBUG=='1';
export const CAPTCHA_ACTIVE = process.env.REACT_APP_CAPTCHA_ACTIVE=='1';
export const RECAPTCHA_KEY = process.env.REACT_APP_RECAPTCHA;
export const GOOGLE_KEY = process.env.REACT_APP_PLACES_KEY;

export const TOKEN_KEY = 'user-token';
export const TOKEN_EVENT = 'user-token';
export const USER_DATA_KEY = 'user-data';
export const USER_DATA_EVENT = 'user-data';
export const ZIPCODE = 'zipcode';
export const BUSINESS_NAME_EVENT = 'business-name-event';
export const BUSINESS_ACCESS_EVENT = 'business-access-event';
export const POSTING_NAME_EVENT = 'posting-name-event';
export const POSTING_ACCESS_EVENT = 'posting-access-event';

var getToken = () : TokenData=>{
	var jsonstr = localStorage.getItem(TOKEN_KEY);
	if(!jsonstr) return null;
	try{
		var data = JSON.parse(jsonstr) as TokenData;
		if(!data) return null;
		if(!data.access) return null;
		var now = moment().unix();
		if(data.expires<=(now+30)) return null;
		return data;
	}catch(e){
		return null;
	}
}

var saveToken = (token_data: TokenData)=>{
	localStorage.setItem(TOKEN_KEY, JSON.stringify(token_data));
	window.dispatchEvent(new Event(TOKEN_EVENT));
}

var logout = ()=>{
	localStorage.removeItem(TOKEN_KEY);
	localStorage.removeItem(USER_DATA_KEY);
	window.dispatchEvent(new Event(USER_DATA_EVENT));
	window.dispatchEvent(new Event(TOKEN_EVENT));
}

interface PostOptions{
	response_type?: ResponseType,
	access_token?: string,
}

async function post<T=any>(endpoint: string, data?: any, opts: PostOptions={}) : Promise<APIResponse<T>>{
	var options = {
		headers: {
			'show-code': DEBUG ? 1 : 0,
			'token': '',
			'Content-Type': 'application/json'
		}
	}
	if(opts?.access_token || opts?.access_token===null){
		options.headers.token = opts.access_token;
	}else{
		var u = getToken();
		if(u?.access){
			options.headers.token = u.access;
		}
		// if(u===null){
		// 	logout();
		// 	return { error: true, code: 'PV-INVCRD', message: 'Las credenciales de accceso no son válidas (LCL-TKNCH-L).' }
		// }
	}
	if(data instanceof FormData){
		options.headers['Content-Type'] = 'multipart/form-data';
	}

	try{
		var ENDPOINT = `${ROOT_URL}/${endpoint}`;
		if(DEBUG) console.log("POST "+ENDPOINT);
		var res = await axios.post(ENDPOINT, data, {
			...options,
			responseType: opts?.response_type || 'json'
		});
		if(DEBUG) console.log(` => Response: ${(res.headers['content-length']/1024).toFixed(2)} KB`);
		if(res.data && res.data.error && res.data.code=='P-TKNE'){
			logout();
		}
		return res.data as APIResponse<T>;
	}catch(err){
		if(DEBUG) console.error(err);
		if((err as any)?.code==="ERR_NETWORK"){
			return {
				error: true, 
				message: 'No se pudo establecer una conexión con el servidor. (API-NONTWK)',
				code: 'API-NONTWK',
			}
		}
		return {
			error: true, 
			message: 'Hubo un error inesperado (API-LCL-1)',
			code: 'API-LCL-1',
		}
	}
}

var CACHE : { [x: string]: APIResponse<any> } = {};
var cachePost = async <T=any>(endpoint: string, key: string, data?: any) : Promise<APIResponse<T>>=>{
	if(CACHE[key]) return CACHE[key] as APIResponse<T>;
	var res = await post(endpoint, data);
	if(!res.error) CACHE[key] = res;
	return res;
}

var getAuthUrl = ()=>{
	return `${PAGE_AUTH}/admin?lt=1&continue=${encodeURIComponent(`${window.location.origin}/auth`)}&redirect=${encodeURIComponent(window.location.href)}`
}

var USER_LOGIN_CACHE : UserLogin = null;

var getLogin = async (force: boolean=false, token: string=null, dispatch_event: boolean=true)=>{
	if(!force && USER_LOGIN_CACHE) return { error: false, data: USER_LOGIN_CACHE, message: '' }

	if(!token){
		var current_token = getToken();
		if(!current_token || current_token.expires<moment().unix()) return { error: true, message: 'El usuario no ha iniciado sesión (LCL-1)' };
	}

	var res = await post<UserLogin>('users/me', {}, {
		access_token: (token || undefined)
	});
	if(res.error) return res;
	USER_LOGIN_CACHE = res.data;
	localStorage.setItem(USER_DATA_KEY, JSON.stringify(res.data));
	localStorage.setItem(ZIPCODE, JSON.stringify(res.data.user.zipcode));
	if(dispatch_event) window.dispatchEvent(new Event(USER_DATA_EVENT));
	return res;
}

var onLoginToken = async (token: string, expiration: number, dispatch_event: boolean=true)=>{
	var res = await getLogin(true, token, false);
	if(!res.error){
		saveToken({ access: token, expires: expiration });
		if(dispatch_event) window.dispatchEvent(new Event(USER_DATA_EVENT));
	}
	return res;
}

var getUserBusiness = async ()=>{
	var res = await cachePost<UserBusiness[]>('users/business', 'USR-BSNLST');
	if(!res.error){
		var evt = new CustomEvent(BUSINESS_NAME_EVENT, { detail: res.data });
		window.dispatchEvent(evt);
	}
	return res;
}

var getBusinessInfo = async (business_id: number)=>{
	var res = await cachePost<{ business_id: number, business_name: string }>('business/info', `BNIF-${business_id}`, { business_id });
	if(!res.error){
		var evt = new CustomEvent(BUSINESS_NAME_EVENT, { detail: [res.data] });
		window.dispatchEvent(evt);
	}
	return res;
}

var editBusiness = async (business_id: number, data: EditBusinessData)=>{
	return post('business/edit', { business_id, ...data });
}

var getBusiness = async (business_id: number, tax_data: boolean = false)=>{
	var res = await cachePost<Business>('business/get', `BNIF-${business_id}`, { business_id, tax_data});
	if(!res.error){
		var evt = new CustomEvent(BUSINESS_NAME_EVENT, { detail: [res.data] });
		window.dispatchEvent(evt);
	}
	return res;
}

var getBusinessUsers = async (business_id: number)=>{
	return post<BusinessUser[]>('business/users/list', { business_id });
}

var addBusinessUser = async (business_id: number, email: string, admin: boolean, access: number[])=>{
	return post<BusinessUser>('business/users/add', { business_id, email, admin, access });
}

var getBusinessAccess = async (business_id: number)=>{
	var res = await post<{
		business_id: number,
		admin: boolean,
		access: number[],
	}>('business/access', { business_id });
	if(!res.error){
		window.dispatchEvent(new CustomEvent(BUSINESS_ACCESS_EVENT, { detail: res.data }));
	}
	return res;
}

var editBusinessUser = async (business_id: number, user_id: number, admin: boolean, access: number[])=>{
	return post('business/users/edit', { business_id, user_id, admin, access });
}

var removeBusinessUser = async (business_id: number, user_id: number)=>{
	return post('business/users/remove', { business_id, user_id });
}

var getBusinessUser = async (business_id: number, user_id: number)=>{
	return post<UserBusinessAccess>('business/users/get', { business_id, user_id });
}

var getBusinessPostings = async (business_id: number, deleted: boolean = false, offset?: number, count?: number)=>{
	var res = await post<Posting[]>('business/postings/list', { business_id, deleted, offset, count });
	if(!res.error){
		window.dispatchEvent(new CustomEvent(POSTING_NAME_EVENT, { detail: res.data.map(a=>({
			posting_id: a.posting_id,
			posting_name: a.posting_name
		}))}));
	}
	return res;
}

interface PostingCreateData{
	posting_name: string,
	location: Location
}

var getPostingList = async (zipcode: number, newest: boolean = false)=>{
	return post('posting/list', { zipcode, newest });
}

var createPosting = async (business_id: number, data: PostingCreateData)=>{
	return post('posting/create', { business_id, ...data });
}

var getEditPosting = async (posting_id: number, location: boolean=false, job_count: boolean=false)=>{
	return post('posting/edit/get', { posting_id, location, job_count });
}

var getPostingInfo = async (posting_id: number)=>{
	return post<{ posting_id: number, posting_name: string, pending_users: number }>('posting/info', { posting_id });
}

var getPublicPosting = async (posting_id: number, applied: boolean = false, invited: boolean = false, joined: boolean = false)=>{
	return post('posting/get', { posting_id, applied, invited, joined });
}

var editPostingLocation = async (posting_id: number, location: Location)=>{
	return post('posting/edit/location', { posting_id, location });
}

var editPostingInfo = async (posting_id: number, data: EditPostingInfo)=>{
	return post('posting/edit/info', { posting_id, ...data });
}

var applyPostingJob = async (job_id: number)=>{
	return post('posting/jobs/apply', { job_id });
}

var editPostingPayment = async (posting_id: number, data: EditPostingPayment)=>{
	return post('posting/edit/payment', { posting_id, ...data });
}

var publishPosting = async (posting_id: number, published: boolean=true)=>{
	return post('posting/publish', { posting_id, published });
}

var getPostingUsers = async (posting_id: number, status: PostingUserStatus, images: boolean=false)=>{
	return post('posting/users/list', { posting_id, status, images });
}

var getPostingInviteUser = async (email: string, posting_id: number)=>{
	return post('posting/users/invite', { email, posting_id });
}

var approveJobUser = async (posting_id: number, job_id: number, user_id: number)=>{
	return post('posting/users/approve', { posting_id, job_id, user_id });
}

var rejectJobUser = async(posting_id: number, job_id: number, user_id: number, reject_message: string)=>{
	return post('posting/users/reject', { posting_id, job_id, user_id, reject_message });
}

var getPostingAccess = async (posting_id: number)=>{
	var res = await post<{
		posting_id: number,
		admin: boolean,
		access: number[]
	}>('posting/access', { posting_id });
	if(!res.error){
		window.dispatchEvent(new CustomEvent(POSTING_ACCESS_EVENT, { detail: res.data }));
	}
	return res;
}

var addPostingImage = async (posting_id: number, image: File)=>{
	var data = new FormData();
	data.append('posting_id', posting_id.toString());
	data.append('image', image);
	return post<{ image_url: string }>('posting/image', data)
}

var addBusinessImage = async (business_id: number, image: File)=>{
	var data = new FormData();
	data.append('business_id', business_id.toString());
	data.append('image', image);
	return post<{ image_url: string }>('business/image/add', data)
}

var getBusinessPayouts = async(business_id: number, offset?: number, count?: number)=>{
	return post('business/payouts/list', { business_id, offset, count });
}

var getPostingPayoutPreparation = async(posting_id: number)=>{
	return post('posting/payouts/preparation', { posting_id });
}

var getPostingPayouts = async(posting_id: number)=>{
	return post('posting/payouts/list', { posting_id });
}

var getPostingPayout = async(payout_id: number)=>{
	return post('posting/payouts/get', { payout_id });
}

interface editPayoutWorker{
	payment_id: number,
	user_id: number,
	amount: number,
	paid: boolean,
	cancelled: boolean,
	message: string,
	user_name: string,
	date_paid?: number,
	date_created: number
}

var editPayoutWorker = async(data: editPayoutWorker)=>{
	return post('posting/payouts/worker/edit', { ...data });
}

var paidPayoutWorker = async(payment_id: number)=>{
	return post('posting/payouts/worker/paid', { payment_id });
}

var cancelWorkerPayout = async(payment_id: number)=>{
	return post('posting/payouts/worker/cancel', { payment_id });
}

var createPostingPayout = async(posting_id: number)=>{
	return post('posting/payouts/create', { posting_id });
}

var getUserPostings = async (simple: boolean = false)=>{
	return post('users/postings', { simple });
}

var addUserId = async (image: File, side: string)=>{
	var data = new FormData();
	data.append('side', side);
	data.append('image', image);
	return post<{ image_url: string }>('users/id/upload', data)
}

var getOnboardingUser = ()=>{
	return post('users/onboarding/get');
}

var onboardingUserInfo = (data: OnboardingUser)=>{
	return post('users/onboarding/info', {
		first_name: data.first_name,
		last_name: data.last_name,
		phone: data.phone,
		curp: data.curp,
		rfc: data.rfc,
	});
}

var onboardingUserFiles = (id: File, address: File)=>{
	var data = new FormData();
	data.append('id', id);
	data.append('address', address);

	return post('users/onboarding/files', data);
}

var onboardingUserAddress = (data: Location)=>{
	return post('users/onboarding/address', data);
}

var businessList = async (offset: number=0, count: number=20, active: boolean=true)=>{
	return post('admin/business/list', { offset, count, active });
}

var getAdminBusiness = async (business_id: number)=>{
	return post('admin/business/get', { business_id });
}

var postingProspectsList = async (posting_id: number)=>{
	return post('posting/prospects', { posting_id });
}

var postingInviteUser = async (posting_id: number, user_id: number)=>{
	return post('posting/prospects/invite', { posting_id, user_id });
}

var getPostingInvitationLink = (token: string)=>{
	return post('posting/invitation/link', { token });
}

var getPostingJobs = (posting_id: number, published: boolean=false)=>{
	return post('posting/jobs/list', { posting_id, published});
}

var createPostingJob = (posting_id: number, jobInfo: CreatePostingJob)=>{
	return post('posting/jobs/create', { posting_id, ...jobInfo });
}

var getJob = (job_id: number, users: boolean=false, invites: boolean=false)=>{
	return post('posting/jobs/get', { job_id, users, invites });
}

var publishJob = async (job_id: number, published: boolean=true)=>{
	return post('posting/jobs/publish', { job_id, published });
}

var editPostingJob = (job_id: number, jobInfo: CreatePostingJob)=>{
	return post('posting/jobs/edit', { job_id, ...jobInfo });
}

var addJobUser = (job_id: number, user_id: number)=>{
	return post('posting/jobs/users/add', { job_id, user_id });
}

var getUserBankAccounts = ()=>{
	return post('account/banks/list');
}

var createUserBankAccount = (form: BankAccountForm)=>{
	return post('account/banks/add', form);
}

var editBankAccount = (account_id: number, form: BankAccountForm)=>{
	return post('account/banks/edit', { account_id, ...form });
}

var setPrimaryAccount = (account_id: number)=>{
	return post('account/banks/primary', { account_id });
}

var getBankAccount = (account_id: number)=>{
	return post<BankAccount>('account/banks/get', { account_id });
}

var getMoneySummary = (offset?: number, count?: number)=>{
	return post<{
		summary: MoneySummary,
		payments: PaymentLog[]
	}>('account/money', { offset, count } );
}

var getMoneyLog = (offset: number=0, count: number=30)=>{
	return post<PaymentLog[]>('account/money/log', { offset, count });
}

var setPaymentBankAccount = (account_id: number)=>{
	return post<{ payments: number[] }>('account/payments/account', { account_id });
}

var getUserInfoStats = ()=>{
	return post<UserInfoStats>('users/me/stats');
}

var getPayouts = (offset?: number, count?: number)=>{
	return post('admin/payouts/list', { offset, count });
}

var getAdminPayout = (payout_id: number)=>{
	return post('admin/payouts/get', { payout_id });
}

var completePayout = (payout_id: number [])=>{
	return post('admin/payouts/complete', { payout_id });
}

var downloadPayoutsZip = async (payouts: number[]) => {
	var res = (await post('admin/payouts/zip', { payouts }, {
		response_type: 'blob'
	}) as any) as Blob;

	var blob = new Blob([res], { type: 'application/zip' });
	var url = window.URL.createObjectURL(blob);
	var a = document.createElement('a');
	a.href = url;
	a.download = `STPLiq-${moment().format('YYYY-MM-DD')}.zip`;
	document.body.appendChild(a);
	a.click();
	window.URL.revokeObjectURL(url);
};

var usersList = (query: string, offset: number=0, count: number=30, approved: boolean=true)=>{
	return post<User []>('admin/users/list', { query, offset, count, approved });
}

var getUser = (user_id: number)=>{
	return post<User>('admin/users/get', { user_id });
}

var getUserMoney = (user_id: number, offset: number=0, count: number=30)=>{
	return post<User>('admin/users/money', { user_id, offset, count});
}

var jobInviteUser = (job_id: number, users: number[]=[])=>{
	return post('posting/jobs/invite', { job_id, users });
}

var getInvitationInfo = (job_id: number)=>{
	return post('posting/jobs/invitation', { job_id });
}

var jobInvitationResponse = (job_id: number, accepted: boolean)=>{
	return post('posting/jobs/invitation/response', { job_id, accepted });
}

var downloadPostingEmployees = async (posting_id: number)=>{
	var res = (await post('posting/employees/download', { posting_id }, {
		response_type: 'blob'
	}) as any) as Blob;
	
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `Empleados-${posting_id}.csv`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	}
}

var downloadPostingApplicants = async (posting_id: number)=>{
	var res = (await post('posting/applicants/download', { posting_id }, {
		response_type: 'blob'
	}) as any) as Blob;
	
	if(res.type==='application/json'){
		return JSON.parse(await res.text());
	}

	const link = document.createElement('a');
	const url = URL.createObjectURL(res);
	link.href = url;
	link.download = `Aplicantes-${posting_id}.csv`;
	link.click();
	link.remove();

	return {
		error: false,
		code: null,
		message: null,
		data: null
	}
}

export default {
	addBusinessImage,
	addBusinessUser,
	addJobUser,
	addPostingImage,
	addUserId,
	applyPostingJob,
	approveJobUser,
	businessList,
	cancelWorkerPayout,
	completePayout,
	createPosting,
	createPostingJob,
	createPostingPayout,
	createUserBankAccount,
	downloadPayoutsZip,
	downloadPostingApplicants,
	downloadPostingEmployees,
	editBankAccount,
	editBusiness,
	editBusinessUser,
	editPayoutWorker,
	editPostingInfo,
	editPostingJob,
	editPostingLocation,
	editPostingPayment,
	getAuthUrl,
	getAdminBusiness,
	getAdminPayout,
	getBankAccount,
	getBusiness,
	getBusinessAccess,
	getBusinessInfo,
	getBusinessPayouts,
	getBusinessPostings,
	getBusinessUser,
	getBusinessUsers,
	getEditPosting,
	getJob,
	getInvitationInfo,
	getLogin,
	getMoneyLog,
	getMoneySummary,
	getOnboardingUser,
	getPayouts,
	getPostingAccess,
	getPostingInfo,
	getPostingInvitationLink,
	getPostingInviteUser,
	getPostingJobs,
	getPostingList,
	getPostingPayout,
	getPostingPayoutPreparation,
	getPostingPayouts,
	getPostingUsers,
	getPublicPosting,
	getUser,
	getUserMoney,
	getUserBankAccounts,
	getUserBusiness,
	getUserInfoStats,
	getUserPostings,
	jobInviteUser,
	jobInvitationResponse,
	logout,
	onboardingUserAddress,
	onboardingUserFiles,
	onboardingUserInfo,
	onLoginToken,
	paidPayoutWorker,
	postingInviteUser,
	postingProspectsList,
	publishJob,
	publishPosting,
	rejectJobUser,
	removeBusinessUser,
	setPaymentBankAccount,
	setPrimaryAccount,
	usersList
}